import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  IconButton,
  Toolbar,
  useTheme,
  Menu,
  MenuItem,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { askAardvarkCopy } from '../utils/userFacingCopy';
import AskAardvarkModal from './AskAardvarkModal';

interface AppHeaderProps {
  onMenuToggle: () => void;
}

export default function AppHeader({ onMenuToggle }: AppHeaderProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [askAardvarkOpen, setAskAardvarkOpen] = useState(false);
  const [roleTemplateName, setRoleTemplateName] = useState<string | null>(null);
  const [loadingRole, setLoadingRole] = useState(false);
  const [logoError, setLogoError] = useState(false);

  const open = Boolean(anchorEl);

  useEffect(() => {
    const loadRoleTemplate = async () => {
      if (!user?.roleTemplateId) {
        setRoleTemplateName(null);
        return;
      }

      try {
        setLoadingRole(true);
        const response = await apiService.get<{ name: string }>(`${API_ENDPOINTS.ROLES.BASE}/${user.roleTemplateId}`);
        setRoleTemplateName(response.data.name);
      } catch (err) {
        console.warn('Failed to load role template:', err);
        setRoleTemplateName(null);
      } finally {
        setLoadingRole(false);
      }
    };

    loadRoleTemplate();
  }, [user?.roleTemplateId]);

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
    navigate('/login', { replace: true });
  };

  if (!user) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: theme.zIndex.drawer + 1,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
      }}
    >
      <Toolbar
        sx={{
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3 },
          // Match AppLayout / Sidebar offset on mobile — must not grow or content sits under the header.
          minHeight: '64px !important',
          height: { xs: 64, sm: 'auto' },
          overflow: { xs: 'hidden', sm: 'visible' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0, flex: 1 }}>
          <IconButton
            color="inherit"
            aria-label="toggle menu"
            onClick={onMenuToggle}
            edge="start"
            sx={{ mr: { xs: 0, sm: 1 }, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
          {!logoError && (
            <Box
              component="img"
              src="/aardvark.svg"
              alt=""
              sx={{
                height: 36,
                width: 'auto',
                objectFit: 'contain',
                flexShrink: 0,
                display: { xs: 'none', sm: 'block' },
              }}
              onError={() => setLogoError(true)}
            />
          )}
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="h5"
              component="h1"
              fontWeight="bold"
              sx={{
                lineHeight: 1.2,
                fontSize: { xs: '0.95rem', sm: '1.5rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              Alula's Performance Management System
            </Typography>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: 1,
                mt: 0.25,
              }}
            >
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                Welcome back, {user.firstName}!
              </Typography>
              {roleTemplateName && (
                <>
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    •
                  </Typography>
                  {loadingRole ? (
                    <CircularProgress size={12} sx={{ color: 'rgba(255,255,255,0.7)' }} />
                  ) : (
                    <Chip
                      label={roleTemplateName}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.7rem',
                        bgcolor: 'rgba(255,255,255,0.2)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                        '& .MuiChip-label': {
                          px: 1,
                        },
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
          <Button
            color="inherit"
            onClick={() => setAskAardvarkOpen(true)}
            startIcon={<ChatBubbleOutlineIcon />}
            aria-label={askAardvarkCopy.buttonTitle}
            title={askAardvarkCopy.buttonTitle}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              flexShrink: 0,
              minWidth: { xs: 40, sm: 'auto' },
              px: { xs: 1, sm: 1.5 },
              '& .MuiButton-startIcon': { mr: { xs: 0, sm: 0.75 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
              {askAardvarkCopy.button}
            </Box>
          </Button>
          <IconButton
            onClick={handleAvatarClick}
            size="small"
            aria-label={askAardvarkCopy.openProfileMenu}
            sx={{
              p: 0,
              '&:hover': {
                opacity: 0.8,
              },
            }}
          >
            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36, cursor: 'pointer' }}>
              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1.5,
                minWidth: 200,
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              },
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <PersonIcon sx={{ mr: 2, fontSize: 20 }} />
              Profile
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      <AskAardvarkModal open={askAardvarkOpen} onClose={() => setAskAardvarkOpen(false)} />
    </Box>
  );
}
